/* eslint-disable node/no-process-env*/
import {
  BigInt,
  Clevertap,
  CommonTrackingProvider,
  FacebookTrackingPixel,
  GoogleAnalytics4,
  GoogleTagManager,
  Hotjar,
  LoggingTrackingTool,
  MsClarity,
  ProductDetails,
  RenderUserConsentDialogType,
  Trackdesk,
  TrackingTool,
  Twitter,
  VWO,
  Yahoo,
} from 'common-tracking';
import { PropsWithChildren } from 'react';

function shouldDisableGtm() {
  if (
    process.env.NEXT_PUBLIC_CHINA !== 'true' ||
    typeof window === 'undefined'
  ) {
    return false;
  }
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const gtm = urlParams.get('gtm');
    if (gtm === 'false') {
      localStorage.setItem('gtm_disabled', 'true');
    }
    return localStorage.getItem('gtm_disabled') === 'true';
  } catch (e) {
    return false;
  }
}

function getLoggingTool() {
  const result: TrackingTool[] = [];
  result.push(new LoggingTrackingTool());
  return result;
}

function getAllConfiguredTools() {
  const result: TrackingTool[] = [];
  if (
    process.env.NEXT_PUBLIC_ENV === 'DEV' ||
    process.env.NEXT_PUBLIC_ENV === 'PREVIEW'
  ) {
    result.push(new LoggingTrackingTool());
  }

  if (process.env.NEXT_PUBLIC_DISABLE_BIG_INT_ID !== 'true') {
    result.push(new BigInt());
  }
  if (process.env.NEXT_PUBLIC_CLEVERTAP_ID) {
    result.push(new Clevertap(process.env.NEXT_PUBLIC_CLEVERTAP_ID));
  }
  if (process.env.NEXT_PUBLIC_FACEBOOK_TRACKING_PIXEL_ID) {
    result.push(
      new FacebookTrackingPixel(
        process.env.NEXT_PUBLIC_FACEBOOK_TRACKING_PIXEL_ID,
      ),
    );
  }

  const isGtmDisabled = shouldDisableGtm();

  if (
    process.env.NEXT_PUBLIC_GA4_TRACKING_ID &&
    process.env.NEXT_PUBLIC_GTMGA4_TRACKING_ID &&
    !isGtmDisabled
  ) {
    result.push(
      new GoogleAnalytics4(
        process.env.NEXT_PUBLIC_GA4_TRACKING_ID,
        process.env.NEXT_PUBLIC_GTMGA4_TRACKING_ID,
      ),
    );
  } else if (process.env.NEXT_PUBLIC_GTMGA4_TRACKING_ID && !isGtmDisabled) {
    result.push(
      new GoogleTagManager(
        process.env.NEXT_PUBLIC_GTMGA4_TRACKING_ID,
        process.env.NEXT_PUBLIC_ENABLE_GTG_FOR_GTM,
      ),
    );
  }
  if (
    process.env.NEXT_PUBLIC_HJ_TRACKING_ID &&
    process.env.NEXT_PUBLIC_HJSV_TRACKING_ID
  ) {
    result.push(
      new Hotjar(
        process.env.NEXT_PUBLIC_HJ_TRACKING_ID,
        process.env.NEXT_PUBLIC_HJSV_TRACKING_ID,
      ),
    );
  }
  if (process.env.NEXT_PUBLIC_MS_CLARITY_ID) {
    result.push(new MsClarity(process.env.NEXT_PUBLIC_MS_CLARITY_ID));
  }
  if (process.env.NEXT_PUBLIC_TWITTER_TRACKING_ID) {
    result.push(new Twitter(process.env.NEXT_PUBLIC_TWITTER_TRACKING_ID));
  }
  if (process.env.NEXT_PUBLIC_VWO_ID) {
    result.push(new VWO(process.env.NEXT_PUBLIC_VWO_ID));
  }
  if (
    process.env.NEXT_PUBLIC_YAHOO_TRACKING_REMARKETING_ID &&
    process.env.NEXT_PUBLIC_YAHOO_CONVERSION_ID &&
    process.env.NEXT_PUBLIC_YAHOO_CONVERSION_YJAD_ID
  ) {
    result.push(
      new Yahoo(
        process.env.NEXT_PUBLIC_YAHOO_TRACKING_REMARKETING_ID,
        process.env.NEXT_PUBLIC_YAHOO_CONVERSION_ID,
        process.env.NEXT_PUBLIC_YAHOO_CONVERSION_YJAD_ID,
      ),
    );
  }
  if (process.env.NEXT_PUBLIC_TRACK_DESK_ID) {
    result.push(new Trackdesk(process.env.NEXT_PUBLIC_TRACK_DESK_ID));
  }
  return result;
}

export function getTrackingToolsConfiguredByEnvironment() {
  const NEXT_PUBLIC_ENV = process.env.NEXT_PUBLIC_ENV;
  if (NEXT_PUBLIC_ENV === 'LOCAL') {
    return getLoggingTool();
  } else {
    return getAllConfiguredTools();
  }
}

const trackingTools = getTrackingToolsConfiguredByEnvironment();

export const AutoTrackingProvider = (
  props: PropsWithChildren<{
    showCookieBanner?: boolean;
    productDetails: ProductDetails;
    renderUserConsentDialog?: RenderUserConsentDialogType;
  }>,
) => {
  return (
    <CommonTrackingProvider
      showCookieBanner={props.showCookieBanner}
      renderUserConsentDialog={props.renderUserConsentDialog}
      trackingTools={trackingTools}
      productDetails={props.productDetails}
    >
      {props.children}
    </CommonTrackingProvider>
  );
};
